// Maps a user's `date_format` preference (stored as a literal sample, e.g.
// '31/12/2024') to the token mask v-calendar's DatePicker needs for
// display/parsing (e.g. 'DD/MM/YYYY'). Keys mirror DATE_FORMATS in
// config/initializers/constants.rb.
const DATE_FORMAT_MASKS = {
  '31/12/2024': 'DD/MM/YYYY',
  '12/31/2024': 'MM/DD/YYYY',
  '2023-12-31': 'YYYY-MM-DD',
  '31.12.2024': 'DD.MM.YYYY',
  '2024/12/31': 'YYYY/MM/DD',
  '31-12-2024': 'DD-MM-YYYY',
}

export function useFormatting() {
  const diableAllDatesAfterXDays = 85
  const total_hours = Array.from({ length: 24 }, (_, i) => i)

  function dateFormatMask(dateFormat) {
    return DATE_FORMAT_MASKS[dateFormat] || 'DD/MM/YYYY'
  }

  // Formats an ISO datetime into the same numeric mask DatePicker.vue's
  // input already shows (e.g. mask 'DD/MM/YYYY' -> '20/08/2026'), evaluated
  // in the given IANA timezone. All six supported masks are just YYYY/MM/DD
  // in different orders with different separators, so plain token
  // substitution covers every one of them.
  function formatDateInMask(isoString, mask, timeZone) {
    if (!isoString) return ''
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(isoString))
    const get = (type) => parts.find((p) => p.type === type)?.value || ''
    return mask.replace('YYYY', get('year')).replace('MM', get('month')).replace('DD', get('day'))
  }

  function generateAndDownloadCSV(dataArray, labels, fileName) {
    const headerColumns = Object.keys(labels)
    const headerRow = headerColumns.map((col) => labels[col].title || col)
    const csvContent = [
      headerRow.join(','),
      ...dataArray.map((data) =>
        headerColumns.map((col) => {
          const value = data[col]
          return (typeof value === 'string' && value.includes(',')) ? `"${value}"` : value
        }).join(',')
      ),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function titleizeString(string) {
    if (!string) return ''
    return string
      .toLowerCase()
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  function generateRandomString(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  // (2, 2023) => "1-2-2023"
  function beginningOfMonthFromMonthNumber(monthNumber, year) {
    return `1-${monthNumber}-${year}`
  }

  function isPresent(txt) {
    return txt !== null && txt !== '' && txt !== undefined && txt !== 'null'
  }

  function copyTextToClipboard(text) {
    navigator.clipboard.writeText(text)
  }

  function strDatetoObject(givenDate) {
    const [day, month, year] = givenDate.split('-')
    return new Date(year, month - 1, day)
  }

  function dateObjectToFormattedStr(givenDate) {
    return givenDate
      .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
      .replace(/\//g, '-')
  }

  function DaysFromNowOn(days) {
    return new Date(Date.now() + days * 24 * 60 * 60 * 1000)
  }

  function formattedHour(hour, timeFormat) {
    let hours = parseInt(hour)
    const minutes = 0
    if (timeFormat === '12_hours') {
      const ampm = hours >= 12 ? 'PM' : 'AM'
      hours = hours % 12 || 12
      return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`
    }
    return `${hours > 9 ? '' : '0'}${hours}:00`
  }

  function toHours(startAt) {
    const hours = []
    for (let i = startAt + 1; i <= 23; i++) hours.push(i)
    return hours
  }

  return {
    diableAllDatesAfterXDays,
    total_hours,
    dateFormatMask,
    formatDateInMask,
    generateAndDownloadCSV,
    titleizeString,
    generateRandomString,
    beginningOfMonthFromMonthNumber,
    isPresent,
    copyTextToClipboard,
    strDatetoObject,
    dateObjectToFormattedStr,
    DaysFromNowOn,
    formattedHour,
    toHours,
  }
}
